import React from 'react'
import { withRouter } from 'react-router-dom'
import PropTypes from 'prop-types'
import { getChatHistoryRequest, getChatIdRequest } from '../api/websocketApi'
import { convertUserMessageToBepFormat } from '../api/messageConverter'
import { generatePostBackObject, getQueryVariable } from '../actions/static'
import widgetStorage from '../actions/storage'
import WebSocketClient from '../api/WebSocketClient'
import { MESSAGE_SIZE } from '../constants'
import { getMessagesFromHistory } from '../helper/getMessagesFromHistory'
import { getPostbackMessage } from '../helper/getPostbackMessage'
import { getGreetingText, sendOpenedGreeting } from '../api/api'
import { detectSiteLanguageOrDefault } from '../app/config'
import { camelToSnakeCase } from '../helper/camelToSnakeCase'

export const WebSocketsSharedContext = React.createContext()
export const WebSocketsSharedConsumer = WebSocketsSharedContext.Consumer

class WebSockets extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      messages: [],
      client: null,
      page: 0,
      loadMore: true,
      initialized: false,
      userData: {
        name: 'Widget user',
        email: '',
        botId: null,
      },
      isSendFiles: false,
      showTyping: false,
    }
    this.websocketClient = new WebSocketClient()
    this.websocketClient.configureEventHandler(this.handleWidgetReceiveEvent)
    this.websocketClient.configureLanguageUpdateHandler(message =>
      this.handleLanguageUpdate(message),
    )
  }

  componentDidMount() {
    document.addEventListener('visibilitychange', this.handleDocumentFocus)
    const chatId = widgetStorage.tryFindChatIdInAnyStorage()
    const isOpen = widgetStorage.isWidgetOpened()

    this.setState({ chatId }, () => {
      if (chatId && !isOpen) {
        this.websocketClient.disconnect()
        this.websocketClient.connect(this.props.botId, chatId, true)
        this.initializeConversation()
      }
    })
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.openWidget !== this.props.openWidget ||
      prevState.userData.chatId !== this.state.userData.chatId
    ) {
      this.setState(
        {
          page: 0,
          loadMore: true,
          messages: [],
          chatId: widgetStorage.tryFindChatIdInAnyStorage(),
          initialized: !this.props.openWidget ? false : this.state.initialized,
        },
        () => {
          if (this.props.openWidget && this.state.userData.chatId) {
            const initiateConversation =
              !this.props.greeting?.payload || !this.props.popupWasShow
            this.websocketClient.disconnect()
            this.websocketClient.connect(
              this.props.botId,
              this.state.userData.chatId,
              initiateConversation,
            )
          }
        },
      )
    }
  }

  componentWillUnmount() {
    this.websocketClient.disconnect()
    this.props.closeWidget()
  }

  handleDocumentFocus = () => {
    if (document.visibilityState === 'visible') {
      const { userData } = this.state
      const { botId, messages, websiteLocation } = this.props
      const messageId = messages[messages.length - 1]?.id
      this.websocketClient.sendStatus(
        'READ',
        messageId,
        botId,
        userData.chatId,
        websiteLocation,
      )
    }
  }

  getChatId = message => {
    const { user, botId, attributes, websiteLocation, greeting, showPopup } =
      this.props
    const savedChatId = widgetStorage.tryFindChatIdInAnyStorage()
    const language = detectSiteLanguageOrDefault(websiteLocation)
    const websiteUrl = new URL(websiteLocation)
    const domain = websiteUrl.host
    const data = {
      botId,
      chatId: savedChatId,
      name: user?.name || 'Widget user',
      email: user?.email || '',
      language: user?.language || 'en',
      domain: domain,
      attributes: attributes.map(att => {
        return {
          value: att.value,
          name: camelToSnakeCase(att.name),
        }
      }),
    }

    getChatIdRequest(data)
      .then(chatId => {
        if (!savedChatId) {
          data.chatId = chatId
          widgetStorage.setChatId(chatId)
        }
        this.websocketClient.configureChatId(chatId)
        this.websocketClient.configureBotId(botId)
        this.setState({ userData: data }, () => {
          this.getChatHistory(chatId)
        })
        return chatId
      })
      .then(chatId => {
        if (greeting && !savedChatId && showPopup) {
          sendOpenedGreeting(botId, greeting.hash, chatId)
        }
        getGreetingText(botId, attributes, language, chatId).then(greeting => {
          this.props.updateGreeting(greeting)
        })
        this.props.setPersistentMenu(botId, chatId)
      })
      .then(() => {
        if (message) {
          const data = generatePostBackObject(message)
          this.postMessage(data)
        }
      })
  }

  sendRefMessage = ref => {
    this.postMessage({
      type: 'postback',
      payload: ref,
    })
    widgetStorage.setRef(ref)
  }

  postMessage = message => {
    const { userData } = this.state
    const { websiteLocation, botId } = this.props

    const data = convertUserMessageToBepFormat(
      userData.chatId,
      message,
      websiteLocation,
    )
    this.websocketClient.sendMessage(data, botId, userData.chatId)
  }

  getChatHistory = chatId => {
    if (this.props.messages.length > 0) {
      return
    }

    getChatHistoryRequest(chatId, this.props.botId, MESSAGE_SIZE).then(res => {
      const loadMore = res && res.length === MESSAGE_SIZE

      this.setState(
        {
          page: this.state.page + 1,
          loadMore,
          messages: res,
        },
        () => {
          if (res && res.length) {
            this.setHistory(res)
          }
        },
      )
    })
  }

  getMoreChatHistory = () => {
    if (this.state.loadMore) {
      getChatHistoryRequest(
        this.state.chatId,
        this.props.botId,
        MESSAGE_SIZE,
        this.state.page,
      )
        .then(res => {
          const loadMore = res && res.length === MESSAGE_SIZE

          this.setState(
            {
              page: this.state.page + 1,
              loadMore,
            },
            () => {
              if (res && res.length) {
                this.setMoreHistory(res)
              }
            },
          )
        })
        .catch(error => console.log(error))
    }
  }

  setHistory(history) {
    this.props.updateMessages(getMessagesFromHistory(history))

    const ref = getQueryVariable('ref')
    if (ref !== 'false' && ref !== widgetStorage.getRef()) {
      this.sendRefMessage(ref)
    }
  }

  setMoreHistory(history) {
    this.setState(
      {
        messages: [...this.state.messages, history],
      },
      () => {
        this.props.updateMessages(
          getMessagesFromHistory(history),
          undefined,
          true,
        )
      },
    )
  }

  handleWidgetReceiveEvent = event => {
    const { actions, isFromBot } = event
    const { processMessage, userData, processTyping } = this.state
    const { setInputField, botId, websiteLocation } = this.props

    const message = event.message || event.customMessage

    if (actions) {
      actions.forEach(action => {
        if (action.type === 'typing_on') {
          processTyping()
        } else if (action.type === 'show_input') {
          setInputField(true)
        } else if (action.type === 'open_feedback') {
          this.props.setFeedbackConfigs({
            ...action['feedback_configs'],
            postAction: action['post_action'],
          })
          this.props.setIsOpenFeedbackModal(true)
        } else if (action.type === 'end_session') {
          this.handleResetChat()
        } else if (action.type === 'open_url') {
          this.props.openUrl(action.url)
        }
      })
    }

    if (message) {
      let copyMessage = Object.assign({}, message)
      copyMessage.isFromBot = !!isFromBot
      this.websocketClient.sendStatus(
        'DELIVERED',
        copyMessage.id,
        botId,
        userData.chatId,
        websiteLocation,
      )
      if (document.visibilityState === 'visible') {
        this.websocketClient.sendStatus(
          'READ',
          copyMessage.id,
          botId,
          userData.chatId,
          websiteLocation,
        )
      }
      if (copyMessage.type === 'postback') {
        copyMessage = getPostbackMessage(message)
      }
      if (isFromBot) {
        if (this.props.isHiddenWidget) {
          this.props.setUnreadMessages(this.props.unreadMessages + 1)
        }
        if (this.props.isHiddenWidget && !this.props.firstUnreadMessageId) {
          this.props.setFirstUnreadMessageId(message.id)
        }
        this.props.setOperatorName(copyMessage?.customerSupportDetails?.name)
      }
      return processMessage([copyMessage])
    }
  }

  handleLanguageUpdate = message => {
    const { botId, updateWidget, setPersistentMenu, setLanguage } = this.props
    const chatId = widgetStorage.tryFindChatIdInAnyStorage()
    const language = message.shortName
    setLanguage(language)
    updateWidget(botId, language)
    setPersistentMenu(botId, chatId)
  }

  initializeConversation = message => {
    if (!this.state.initialized) {
      this.setState(
        {
          processTyping: this.receiveTyping,
          processMessage: this.receiveMessage,
          initialized: true,
        },
        () => this.getChatId(message),
      )
    }
  }

  userInteractionPostMessage = data => {
    widgetStorage.setRef(null)
    this.postMessage(data)
  }

  receiveTyping = () => {
    if (!this.state.showTyping) {
      this.setState({ showTyping: true })
    }
  }

  receiveMessage = message => {
    this.props.updateMessages(message)
    this.setState({ showTyping: false })
  }

  handleResetChat = () => {
    const { resetChat } = this.props
    resetChat()
    this.websocketClient.disconnect()
  }

  render() {
    return (
      <WebSocketsSharedContext.Provider
        value={{
          language: this.props.language,
          postMessage: this.userInteractionPostMessage,
          connected: this.state?.client?.connected,
          startData: this.state?.userData,
          chatId: this.state.userData?.chatId,
          botId: this.props.botId,
          showTyping: this.state.showTyping,
          initializeConversation: this.initializeConversation,
          getMoreChatHistory: this.getMoreChatHistory,
          handleResetChat: this.handleResetChat,
        }}>
        {this.props.children}
      </WebSocketsSharedContext.Provider>
    )
  }
}

WebSockets.propTypes = {
  user: PropTypes.object,
  onConnect: PropTypes.func,
}

export default withRouter(WebSockets)
